import type { FC } from 'react';
import { Suspense, useEffect } from 'react';
import { useIntl } from 'react-intl';

import type { Company } from '@xing-com/crate-common-graphql-types';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';

import {
  CompanyLoggedout,
  CompanyLoggedoutHeadline,
} from '../../components/company-loggedout';
import { CompanyCardsSkeletonLoggedoutPage } from '../../components/skeleton';
import {
  LOGGED_IN_CARDS_PER_PAGE,
  LOGGED_OUT_CARDS_PER_PAGE,
} from '../../config';
import { usePagination } from '../../hooks/use-pagination';
import { useRecentlyClaimedCompanies } from '../../hooks/use-recently-claimed-companies';
import { useTrackPage } from '../../hooks/use-track-page';
import { scrollToTop } from '../../utils';
import {
  pagesTrackingName,
  useNwtTrackingCompanies,
} from '../../utils/tracking';

const RecentlyClaimedCompaniesInner: FC = () => {
  const { isLoggedIn } = useLoginState();

  const trackNwtEvent = useNwtTrackingCompanies();

  useEffect(() => {
    trackNwtEvent({
      eventSchema: 'basic',
      page: 'companies/new_companies',
      event: 'viewed_screen',
    });
    scrollToTop();
  }, []);

  const limit = isLoggedIn
    ? LOGGED_IN_CARDS_PER_PAGE
    : LOGGED_OUT_CARDS_PER_PAGE;

  const { changePage, activePage } = usePagination();

  const { data, error } = useRecentlyClaimedCompanies({
    limit,
    offset: limit * (activePage - 1),
  });

  useTrackPage({ pageName: pagesTrackingName.recentlyClaimed });
  const { collection, total } = data?.companiesRecentlyClaimed ?? {};

  return (
    <CompanyLoggedout
      companies={collection?.map((item) => item?.company as Company)}
      total={total}
      authenticated={isLoggedIn}
      limit={limit}
      activePage={activePage}
      changePage={changePage}
      error={error}
    />
  );
};

export const RecentlyClaimedCompanies: FC = () => {
  const intl = useIntl();
  const headerText = intl.formatMessage({
    id: 'CP_100_LATELY_BOUGHT_CPS_HEADLINE_NEW',
  });
  return (
    <>
      <CompanyLoggedoutHeadline
        headerText={headerText}
        companyType="recently-claimed"
      />
      <Suspense fallback={<CompanyCardsSkeletonLoggedoutPage />}>
        <RecentlyClaimedCompaniesInner />
      </Suspense>
    </>
  );
};
